import { ref } from 'vue'
import { echoes } from '@/composables/echo/useEchoes.js'
import { Toaster } from '@/models/utils/Toaster.js'
import { Banner } from '@/models/utils/Toaster.js'
import { useAuthStore } from '@/stores/authStore.js'
import { Tenants } from '@/models/tenants/Tenants.js'

/**

 Notes:
 	Calls to public, private, presence will check if the channel is currently subscribed to.
 		- If found it will be reused which is necessary, making a new one causes issues.
 			- Any matching events will be dropped and readded

 	Channels can have multiple events listened to under them.

 	Presence Channel
 		- Not included as part of reverb's Redis scaling so only works between instances.
		- Scheduler used to update user count across reverb instances.

 */

export class SystemEchoes
{
	debug_eh = true
	authStore = null;
	toaster = null;
	banner = null;

	channel_system_message = "system";
	event_system_message = ".system.message";
	event_system_update = ".system.update";//Used to show multiple events under one channel
	event_presence_updated = ".presence.updated";//Cluster-wide presence roster, sent on the system channel (see PresenceAggregationService in portal-app-service)

	channel_notification_role = "notification.role";
	event_notification_role = ".notification.role";

	channel_notification_user = "notification.user";
	event_notification_user = ".notification.user";

	presence_user_connected = "system.user.connected";

	/**
	 * Connected-users roster, grouped by tenant_id: { [tenant_id]: { ids: Set<number>, tenant: Tenant, users: Array<{id, name, tenant_id}> } }.
	 * Populated from the '.presence.updated' event on the system channel (see systemMessageConnect) rather than
	 * from the system.user.connected presence channel's here()/joining()/leaving(), which only reflects
	 * connections on whichever Reverb replica the client itself is on and is unreliable across replicas.
	 */
	connectedUsers = ref({});

	constructor()
	{
		this.toaster = new Toaster();
		this.banner = new Banner();
	}

	//#region Public Functions

	//#region System
	/**
	 * Reconnect the user to all base channels (system message, notifications)
	 */
	systemConnect()
	{
		if (this.#isDeveloper()) console.log('SystemEchoes', 'systemConnect');

		this.userPresenceConnect();
		this.systemMessageConnect();
		this.notificationConnect();
	}

	/**
	 * Disconnect the user from the System Message and System Connected channels
	 */
	systemDisconnect()
	{
		if (this.#isDeveloper()) console.log('SystemEchoes', 'systemDisconnect');
		this.userPresenceDisconnect();
		this.systemMessageDisconnect();
		this.notificationDisconnect();

		echoes.leaveAllChannels();
	}
	//#endregion System

	//#region User Presence

	/**
	 * Presence channel used to track connected users.
	 * Adds "presence-" to channel name (Ex: presence-system.user.connected)
	 * Note: Not included as part of reverb's Redis scaling so only works between instances.
	 * Scheduler used to update user count across reverb instances.
	 * @returns {import('laravel-echo').PusherChannel<'reverb'>}
	 */
	userPresenceConnect()
	{
		return echoes.presence(this.presence_user_connected);
	}

	/**
	 * Disconnect from presence channel.
	 */
	userPresenceDisconnect()
	{
		echoes.leave(this.presence_user_connected)
	}

	//#endregion User Presence

	//#region System Message
	/**
	 * Connect to the system channel
	 */
	systemMessageConnect()
	{
		const events = [
			{
				'event': this.event_system_message,
				'callback': (p_event) =>
				{
					this.banner.info(p_event.p_message);
				}
			},
			{
				'event': this.event_system_update,
				'callback': (p_event) =>
				{
					console.log("Event " + this.event_system_update + ": " + p_event.p_message);
				}
			}
		];

		if (this.#isDeveloper())
		{
			//Only developers will need to see that other users have connected
			events.push({
				'event': this.event_presence_updated,
				'callback': (p_event) =>
				{
					this.connectedUsers.value = this.#buildConnectedUsersByTenant(p_event.p_members);
				}
			});
		}


		echoes.public(this.channel_system_message, events);
	}

	/**
	 * Disconnect from system message channel
	 */
	systemMessageDisconnect()
	{
		echoes.leave(this.channel_system_message);
	}
	//#endregion System Message

	//#region Notifications
	/**
	 * Connect user to all notification channels
	 */
	notificationConnect()
	{
		this.notificationUserConnect();
		this.notificationRoleConnect();
	}

	/**
	 * Disconnect user from all notification channels
	 */
	notificationDisconnect()
	{
		this.notificationUserDisconnect();
		this.notificationRoleDisconnect();
	}

	/**
	 * Connects user with notification user channel
	 */
	notificationRoleConnect()
	{
		if (this.#isDeveloper()) console.log('SystemEchoes', 'notificationRoleConnect', 'role_id:', this.#roleId());
		if (this.#roleId() > 0)
		{
			echoes.private(this.#notificationRoleChannel(this.#roleId()), [{
				'event': this.event_notification_role,
				'callback': (p_event) =>
				{
					this.banner.info("Event " + this.event_notification_role + ": " + JSON.stringify(p_event.p_data));
				}
			}]);
		}
	}

	/**
	 * Disconnects user from notification user channel
	 */
	notificationRoleDisconnect()
	{
		echoes.leave(this.#notificationRoleChannel(this.#roleId()));
	}

	/**
	 * Connects user with notification user channel
	 */
	notificationUserConnect()
	{
		if (this.#isDeveloper()) console.log('SystemEchoes', 'notificationUserConnect', 'user_id:', this.#userId());
		if (this.#userId() > 0)
		{
			echoes.private(this.#notificationUserChannel(this.#userId()),
				[{
					'event': this.event_notification_user,
					'callback': (p_event) =>
					{
						if (this.#isDeveloper()) console.log(this.event_notification_user, p_event)
						this.banner.info("Event " + this.event_notification_user + ": " + JSON.stringify(p_event.p_data));
					}
				}
				]
			);
		}
	}

	/**
	 * Disconnects user from notification user channel
	 */
	notificationUserDisconnect()
	{
		echoes.leave(this.#notificationUserChannel(this.#userId()));
	}
	//#endregion Notifications

	//#endregion Public Functions

	//#region Private Functions (private to class)
	#isDeveloper ()
	{
		try
		{
			if (this.debug_eh && import.meta.env.VITE_ENV === 'dev')
			{
				return true;
			}
			else if (this.#authStore().isDeveloper)
			{
				return true;
			}
		}
		catch(e)
		{
			//do nothing
			if (import.meta.env.VITE_ENV === 'dev')
			{
				console.error('Error in useSystemEchoes.#isDeveloper', e);
			}
		}
		return false;
	}


	#authStore ()
	{
		if (!this.authStore) this.authStore = useAuthStore()
		return this.authStore
	}

	/**
	 * Get the current role id for the active organization.
	 * @returns {number}
	 */
	#roleId()
	{
		let role_id = 0;
		try
		{
			if (!this.#authStore().isLoggedIn)
			{
				return role_id
			}
			if (this.#authStore().getActiveOrganization)
			{
				role_id = this.authStore.getActiveOrganization.user_role_id
			}
		}
		catch(e)
		{
			console.error(e)
		}

		return role_id;
	}

	/**
	 * Get the current user id;
	 * @returns {number}
	 */
	#userId()
	{
		let user_id = 0;
		try
		{
			if (!this.#authStore().isLoggedIn)
			{
				return user_id
			}
			if (this.#authStore()?.getAuth?.user())
			{
				user_id = this.#authStore().getAuth.user().id
			}
		}
		catch(e)
		{
			console.error(e)
		}

		return user_id;
	}

	#notificationRoleChannel(p_role_id)
	{
		return this.channel_notification_role + "." + p_role_id;
	}
	#notificationUserChannel(p_user_id)
	{
		return this.channel_notification_user + "." + p_user_id;
	}

	/**
	 * Groups a flat presence-roster member list into the { [tenant_id]: { ids, tenant, users } } shape
	 * consumed by SuperAdminPage.vue/PortalSettingsPage.vue. Each call is a full snapshot from the backend,
	 * so this replaces the previous incremental here()/joining()/leaving() add/remove bookkeeping.
	 * @param {Array<{id: number, name: string, tenant_id: number}>} p_members
	 * @returns {Object}
	 */
	#buildConnectedUsersByTenant(p_members)
	{
		const connected_users = {};

		for (const user of (p_members ?? []))
		{
			if (!connected_users[user.tenant_id])
			{
				connected_users[user.tenant_id] = { ids: new Set(), tenant: Tenants.getTenantById(user.tenant_id), users: [] };
			}
			connected_users[user.tenant_id].ids.add(user.id);
			connected_users[user.tenant_id].users.push(user);
		}

		return connected_users;
	}

	//#endregion Private Functions

	//#region Temp Functions
	/**
	 * Start listening to System Message event on System channel
	 */
	systemMessageEventConnect()
	{
		echoes.public(this.channel_system_message,
			[{
				'event': this.event_system_message,
				'callback': (p_event) =>
				{
					this.banner.info("System Message", p_event.p_message);
				}
			}]);
	}

	/**
	 * Stop Listening to system message event on system message channel
	 */
	systemMessageEventStopListening()
	{
		echoes.stopListening(this.channel_system_message, this.event_system_message);
	}

	/**
	 * Start listening to system update event on system message channel
	 */
	systemUpdateConnect()
	{
		echoes.public(this.channel_system_message,
			[
				{
					'event': this.event_system_update,
					'callback': (p_event) =>
					{
						this.banner.info('Update:' + p_event.p_message);
					}
				}
			]);
	}

	/**
	 * Stop Listening to system update event on system message channel
	 */
	systemUpdateDisconnect()
	{
		echoes.stopListening(this.channel_system_message, this.event_system_update);
	}
	//#endregion Temp Functions
}


export const systemEchoes = new SystemEchoes();